import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from '../../util/Toast';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../util/SettingsProvider';
export const ProfilePage = ({ userId }) => {
    // Dynamic Profile Data State
    const [profile, setProfile] = useState({
        name: '',
        email: '',
        number: '',
        upi: '',
        tgLink: '',
        bal: 0
    });

    const [upiId, setUpiId] = useState('');
    const [telegramLink, setTelegramLink] = useState('');

    // Password state management
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [isPasswordVerified, setIsPasswordVerified] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isSavedDetails, setIsSavedDetails] = useState(false);
    const [isPasswordUpdated, setIsPasswordUpdated] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const navigate = useNavigate();
    // Login History State & Pagination
    const [loginHistory, setLoginHistory] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;
    const settings = useSettings();
    useEffect(() => { document.title = `Profile | ${settings?.settings?.site_name || ''}`; }, [settings?.settings?.site_name]);
    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    };

    useEffect(() => {
        const fetchUserData = async () => {
            const token = getCookie('authToken');
            try {
                const baseUrl = process.env.REACT_APP_API_URL || '';

                const response = await axios.get(`${baseUrl}/api/profile`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.data && response.data.data) {
                    const apiData = response.data.data;

                    if (apiData.user) {
                        setProfile(apiData.user);
                        setUpiId(apiData.user.upi || '');
                        setTelegramLink(apiData.user.tgLink || '');
                    }

                    if (Array.isArray(apiData.history)) {
                        setLoginHistory(apiData.history);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch profile data:", error);
                toast.error(error.response?.data?.msg || "An unexpected error occurred.");
                if (error.response && error.response.status === 401) {
                    navigate('/login');
                }
            }
        };
        fetchUserData();
    }, [navigate]);

    const handleSaveDetails = async (e) => {
        e.preventDefault();
        setErrorMsg('');
        const token = getCookie('authToken');
        try {
            const baseUrl = process.env.REACT_APP_API_URL || '';

            await axios.post(`${baseUrl}/api/profile/update-details`, {
                upi: upiId,
                telegram: telegramLink
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            setProfile(prev => ({ ...prev, upi: upiId, tgLink: telegramLink }));
            setIsSavedDetails(true);
            setTimeout(() => setIsSavedDetails(false), 3000);
        } catch (err) {
            console.error('Error updating details:', err);
            setErrorMsg(err.response?.data?.message || 'Failed to update details');
        }
    };

    const handleVerifyCurrentPassword = async (e) => {
        e.preventDefault();
        if (!currentPassword) return;
        setIsVerifying(true);
        setErrorMsg('');
        const token = getCookie('authToken');
        try {
            const baseUrl = process.env.REACT_APP_API_URL || '';

            const response = await axios.post(`${baseUrl}/api/profile/verify-password`, {
                password: currentPassword
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            setIsVerifying(false);
            if (response.data && (response.data.success || response.data.status)) {
                setIsPasswordVerified(true);
            } else {
                setErrorMsg(response.data.message || 'Incorrect password');
            }
        } catch (err) {
            setIsVerifying(false);
            console.error('Error verifying password:', err);
            setErrorMsg(err.response?.data?.message || 'Verification failed');
        }
    };

    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        if (!newPassword) return;
        setErrorMsg('');
        const token = getCookie('authToken');
        try {
            const baseUrl = process.env.REACT_APP_API_URL || '';

            await axios.post(`${baseUrl}/api/profile/update-password`, {
                newPassword
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            setIsPasswordUpdated(true);
            setNewPassword('');
            setCurrentPassword('');
            setIsPasswordVerified(false);
            setTimeout(() => setIsPasswordUpdated(false), 3000);
        } catch (err) {
            console.error('Error updating password:', err);
            setErrorMsg(err.response?.data?.message || 'Failed to update password');
        }
    };

    // Pagination calculations
    const totalPages = Math.ceil(loginHistory.length / itemsPerPage) || 1;
    const paginatedHistory = loginHistory.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <div className="w-full pt-16 sm:pt-24 lg:pt-28 pb-16 px-4 sm:px-8 lg:pl-80 relative overflow-hidden font-sans min-h-screen bg-zinc-950 text-zinc-100">
            <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(16px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes pulseGlow {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(1.08); }
        }

        @keyframes borderBeam {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        @keyframes floatTitle {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }

        .animate-spring-in {
          animation: springIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .ambient-glow {
          animation: pulseGlow 8s ease-in-out infinite;
        }

        .hero-border-glow {
          background: linear-gradient(90deg, rgba(16, 185, 129, 0.4), rgba(20, 184, 166, 0.1), rgba(16, 185, 129, 0.4));
          background-size: 200% 200%;
          animation: borderBeam 6s ease infinite;
        }

        .animate-float-title {
          animation: floatTitle 4s ease-in-out infinite;
        }

        .micro-interaction {
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .micro-interaction:hover {
          transform: translateY(-2px);
        }

        .micro-interaction:active {
          transform: translateY(0px) scale(0.98);
        }
      `}</style>

            {/* Fluid ambient background lighting matching Offers theme */}
            <div className="absolute top-10 left-1/3 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none ambient-glow" />
            <div className="absolute bottom-10 right-10 w-80 h-80 bg-teal-500/10 rounded-full blur-[100px] pointer-events-none ambient-glow" />

            <div className="max-w-[1000px] mx-auto space-y-8 relative z-10">

                {/* Main Profile Header Card (Highlighted Feature Section) */}
                <div className="relative p-[1px] rounded-[2.6rem] hero-border-glow shadow-2xl shadow-emerald-950/40 animate-spring-in group">
                    <div className="bg-zinc-900/90 backdrop-blur-xl rounded-[2.5rem] p-6 sm:p-8 relative overflow-hidden">

                        {/* Decorative background glow accents inside card */}
                        <div className="absolute -top-24 -right-24 w-64 h-64 bg-emerald-500/15 rounded-full blur-3xl pointer-events-none group-hover:bg-emerald-500/25 transition-all duration-700" />
                        <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

                        <div className="relative z-10 flex flex-col items-center sm:flex-row sm:items-start gap-6 sm:gap-8">
                            {/* Animated Avatar */}
                            <div className="relative shrink-0">
                                <div className="absolute -inset-1 rounded-[2rem] bg-gradient-to-r from-emerald-500 to-teal-400 blur opacity-40 group-hover:opacity-100 transition duration-500 group-hover:scale-105" />
                                <div className="relative w-24 h-24 sm:w-28 sm:h-28 bg-gradient-to-tr from-emerald-600 via-emerald-500 to-teal-400 rounded-3xl flex items-center justify-center text-4xl font-black text-zinc-950 shadow-2xl shadow-emerald-500/30 border border-emerald-300/40 group-hover:scale-105 transition-all duration-300">
                                    {profile.name ? profile.name.charAt(0).toUpperCase() : ''}
                                </div>
                                <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-emerald-400 border-2 border-zinc-950"></span>
                                </span>
                            </div>

                            <div className="flex-1 text-center sm:text-left space-y-6 w-full">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                    <div>
                                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                            <p className="text-emerald-400 text-[10px] font-black uppercase tracking-widest">Active Profile</p>
                                        </div>
                                        {/* Fluid floating animation applied to user title */}
                                        <h2 className="text-3xl sm:text-4xl font-black text-zinc-100 tracking-tight animate-float-title inline-block">
                                            {profile.name}
                                        </h2>
                                    </div>
                                </div>

                                {/* Info Cards Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Phone Card */}
                                    <div className="flex items-center gap-3.5 p-4 bg-zinc-950/70 backdrop-blur-md rounded-2xl border border-zinc-800/80 micro-interaction hover:border-emerald-500/30">
                                        <div className="p-2.5 bg-emerald-500/10 text-emerald-400 rounded-xl border border-emerald-500/20 shrink-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />
                                            </svg>
                                        </div>
                                        <div className="overflow-hidden">
                                            <p className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider">Number</p>
                                            <p className="text-sm font-bold text-zinc-200 truncate">{profile.number}</p>
                                        </div>
                                    </div>

                                    {/* Email Card */}
                                    <div className="flex items-center gap-3.5 p-4 bg-zinc-950/70 backdrop-blur-md rounded-2xl border border-zinc-800/80 micro-interaction hover:border-emerald-500/30">
                                        <div className="p-2.5 bg-emerald-500/10 text-emerald-400 rounded-xl border border-emerald-500/20 shrink-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
                                                <rect x="2" y="4" width="20" height="16" rx="2" />
                                            </svg>
                                        </div>
                                        <div className="overflow-hidden">
                                            <p className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider">Email Address</p>
                                            <p className="text-sm font-bold text-zinc-200 truncate">{profile.email}</p>
                                        </div>
                                    </div>

                                    {/* Linked UPI ID Card */}
                                    <div className="flex items-center gap-3.5 p-4 bg-zinc-950/70 backdrop-blur-md rounded-2xl border border-zinc-800/80 micro-interaction hover:border-emerald-500/30">
                                        <div className="p-2.5 bg-emerald-500/10 text-emerald-400 rounded-xl border border-emerald-500/20 shrink-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect width="20" height="14" x="2" y="5" rx="2" />
                                                <line x1="2" x2="22" y1="10" y2="10" />
                                            </svg>
                                        </div>
                                        <div className="overflow-hidden">
                                            <p className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider">Linked UPI ID</p>
                                            <p className="text-sm font-bold text-zinc-200 truncate">{profile.upi}</p>
                                        </div>
                                    </div>

                                    {/* Wallet Balance Card */}
                                    <div className="flex items-center gap-3.5 p-4 bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-400 hover:to-teal-300 rounded-2xl border border-emerald-300/50 shadow-lg shadow-emerald-500/20 text-zinc-950 micro-interaction transition-all">
                                        <div className="p-2.5 bg-zinc-950/20 text-zinc-950 rounded-xl shrink-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 1 1-1v-2a1 1 0 0 0-1-1" />
                                                <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
                                            </svg>
                                        </div>
                                        <div className="overflow-hidden">
                                            <p className="text-[10px] font-black uppercase tracking-wider text-zinc-900/80">Wallet Balance</p>
                                            <p className="text-base font-black text-zinc-950 truncate">₹{profile.bal}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Payment & Social Form Card */}
                <div className="bg-zinc-900/80 backdrop-blur-md border border-zinc-800/80 rounded-[2.5rem] p-6 sm:p-8 shadow-2xl relative overflow-hidden animate-spring-in">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="p-3 bg-emerald-500/10 text-emerald-400 rounded-xl border border-emerald-500/20">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
                                <path d="m21.854 2.147-10.94 10.939" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-black tracking-tight text-zinc-100">Payment &amp; Social</h3>
                    </div>

                    <form onSubmit={handleSaveDetails} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* UPI ID Input */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-zinc-400 uppercase ml-2 tracking-wider">UPI ID</label>
                                <div className="relative group">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 group-focus-within:text-emerald-400 transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="4" x2="20" y1="9" y2="9" />
                                        <line x1="4" x2="20" y1="15" y2="15" />
                                        <line x1="10" x2="8" y1="3" y2="21" />
                                        <line x1="16" x2="14" y1="3" y2="21" />
                                    </svg>
                                    <input
                                        placeholder="vpa@bank"
                                        className="w-full pl-11 pr-4 py-3 bg-zinc-950 border border-zinc-800 rounded-2xl text-zinc-100 placeholder-zinc-500 text-sm focus:outline-none focus:border-emerald-500/50 focus:ring-4 focus:ring-emerald-500/10 transition-all font-medium micro-interaction"
                                        type="text"
                                        value={upiId}
                                        onChange={(e) => setUpiId(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Telegram Link Input */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-zinc-400 uppercase ml-2 tracking-wider">Telegram Link</label>
                                <div className="relative group">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 group-focus-within:text-emerald-400 transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
                                        <path d="m21.854 2.147-10.94 10.939" />
                                    </svg>
                                    <input
                                        placeholder="https://t.me/yourchannel"
                                        className="w-full pl-11 pr-4 py-3 bg-zinc-950 border border-zinc-800 rounded-2xl text-zinc-100 placeholder-zinc-500 text-sm focus:outline-none focus:border-emerald-500/50 focus:ring-4 focus:ring-emerald-500/10 transition-all font-medium micro-interaction"
                                        type="text"
                                        value={telegramLink}
                                        onChange={(e) => setTelegramLink(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            <button
                                type="submit"
                                className="w-full md:w-auto px-8 py-3 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-xl text-xs font-bold shadow-lg shadow-emerald-500/20 transition-all micro-interaction active:scale-95 flex items-center justify-center gap-2"
                            >
                                Save Details
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
                                    <path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7" />
                                    <path d="M7 3v4a1 1 0 0 0 1 1h7" />
                                </svg>
                            </button>

                            {isSavedDetails && (
                                <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3.5 py-2 rounded-xl">
                                    ✓ Saved successfully!
                                </span>
                            )}
                        </div>
                    </form>
                </div>

                {/* Security Settings Card (Two-Step Verification Password Update Flow) */}
                <div className="bg-zinc-900/80 backdrop-blur-md border border-zinc-800/80 rounded-[2.5rem] p-6 sm:p-8 shadow-2xl relative overflow-hidden animate-spring-in">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="p-3 bg-emerald-500/10 text-emerald-400 rounded-xl border border-emerald-500/20">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-black tracking-tight text-zinc-100">Security Settings</h3>
                    </div>

                    {errorMsg && (
                        <div className="mb-4 text-xs font-bold text-rose-400 bg-rose-500/10 border border-rose-500/20 px-3.5 py-2 rounded-xl inline-block">
                            {errorMsg}
                        </div>
                    )}

                    {!isPasswordVerified ? (
                        /* Step 1: Verify Current Password */
                        <form onSubmit={handleVerifyCurrentPassword} className="space-y-4">
                            <p className="text-sm text-zinc-400 font-medium">Please enter your current password to verify your identity before changing it.</p>

                            <div className="flex flex-col sm:flex-row gap-3">
                                <input
                                    required
                                    placeholder="Current Password"
                                    className="flex-1 px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-2xl text-zinc-100 placeholder-zinc-500 text-sm focus:outline-none focus:border-emerald-500/50 focus:ring-4 focus:ring-emerald-500/10 transition-all font-medium micro-interaction"
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                />

                                <button
                                    type="submit"
                                    disabled={isVerifying}
                                    className="px-8 py-3 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-xl text-xs font-bold shadow-lg shadow-emerald-500/20 transition-all micro-interaction active:scale-95 flex items-center justify-center gap-2 shrink-0 disabled:opacity-50"
                                >
                                    {isVerifying ? 'Verifying...' : 'Verify Password'}
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                        <polyline points="22 4 12 14.01 9 11.01" />
                                    </svg>
                                </button>
                            </div>
                        </form>
                    ) : (
                        /* Step 2: Set New Password (Appears after verification) */
                        <form onSubmit={handleUpdatePassword} className="space-y-4 animate-spring-in">
                            <div className="flex items-center gap-2 text-xs font-bold text-emerald-400 mb-1">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                                Identity Verified. Enter your new password below.
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3">
                                <input
                                    required
                                    placeholder="New Password"
                                    className="flex-1 px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-2xl text-zinc-100 placeholder-zinc-500 text-sm focus:outline-none focus:border-emerald-500/50 focus:ring-4 focus:ring-emerald-500/10 transition-all font-medium micro-interaction"
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />

                                <button
                                    type="submit"
                                    className="px-8 py-3 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-xl text-xs font-bold shadow-lg shadow-emerald-500/20 transition-all micro-interaction active:scale-95 flex items-center justify-center gap-2 shrink-0"
                                >
                                    Update Password
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 12h14" />
                                        <path d="m12 5 7 7-7 7" />
                                    </svg>
                                </button>
                            </div>

                            {isPasswordUpdated && (
                                <p className="text-xs font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3.5 py-2 rounded-xl inline-block">
                                    ✓ Password updated successfully!
                                </p>
                            )}
                        </form>
                    )}
                </div>

                {/* Login History Section with Pagination & Session Badges */}
                <div className="bg-zinc-900/80 backdrop-blur-md border border-zinc-800/80 rounded-[2.5rem] shadow-2xl overflow-hidden animate-spring-in">
                    <div className="p-6 sm:p-8 bg-zinc-950/60 border-b border-zinc-800/80 flex items-center justify-between">
                        <h3 className="text-lg font-black tracking-tight text-zinc-100">Login History</h3>
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-zinc-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                        </svg>
                    </div>

                    <div className="divide-y divide-zinc-800/60">
                        {paginatedHistory.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-5 sm:p-6 hover:bg-emerald-500/[0.03] transition-all duration-300 group">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-zinc-950 rounded-2xl flex items-center justify-center text-zinc-500 group-hover:text-emerald-400 border border-zinc-800 shadow-inner group-hover:border-emerald-500/30 group-hover:scale-110 transition-all duration-300">
                                        {item.device === 'Mobile' ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
                                                <path d="M12 18h.01" />
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect width="20" height="14" x="2" y="3" rx="2" />
                                                <line x1="8" x2="16" y1="21" y2="21" />
                                                <line x1="12" x2="12" y1="17" y2="21" />
                                            </svg>
                                        )}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <p className="font-bold text-zinc-100 text-sm group-hover:text-emerald-400 transition-colors">
                                                {item.browser} on {item.os}
                                            </p>
                                            {idx === 0 && currentPage === 1 && (
                                                <span className="px-2 py-0.5 bg-emerald-500 text-zinc-950 rounded-md text-[9px] font-black uppercase tracking-wider">
                                                    Current Session
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-[11px] font-medium text-zinc-500 flex items-center gap-1 mt-0.5">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                                                <circle cx="12" cy="10" r="3" />
                                            </svg>
                                            {item.ip} • {item.date}
                                        </p>
                                    </div>
                                </div>
                                <span className="px-3.5 py-1.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-xl text-[10px] font-bold uppercase tracking-wider shadow-sm shadow-emerald-500/5 group-hover:scale-105 transition-transform duration-300">
                                    Authorized
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* Pagination Footer Controls */}
                    <div className="p-4 sm:p-6 bg-zinc-950/40 border-t border-zinc-800/80 flex items-center justify-between text-xs text-zinc-400">
                        <p className="font-medium">
                            Page <span className="text-zinc-100 font-bold">{currentPage}</span> of <span className="text-zinc-100 font-bold">{totalPages}</span>
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                className="px-3.5 py-1.5 bg-zinc-950 border border-zinc-800 rounded-xl text-zinc-300 hover:text-emerald-400 hover:border-emerald-500/30 disabled:opacity-40 disabled:hover:text-zinc-300 disabled:hover:border-zinc-800 transition-all font-bold micro-interaction"
                            >
                                Previous
                            </button>
                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                className="px-3.5 py-1.5 bg-zinc-950 border border-zinc-800 rounded-xl text-zinc-300 hover:text-emerald-400 hover:border-emerald-500/30 disabled:opacity-40 disabled:hover:text-zinc-300 disabled:hover:border-zinc-800 transition-all font-bold micro-interaction"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default ProfilePage;